import { Link } from '@tanstack/react-router';
import { ArrowLeft, Lock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  NOTIFICATION_CATEGORY_DESCRIPTIONS,
  NOTIFICATION_CATEGORY_LABELS,
  escalationsIn,
  isNotificationOn,
  type CategoryPreference,
  type ModuleState,
  type NotificationCategory,
  type NotificationPreferences,
} from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { Tabs, type TabItem } from '../../../components/Tabs';
import { ServerErrorState } from '../../../components/system-states/SystemState';
import { useModules } from '../../../lib/modules';
import { usePreferences, useUpdatePreferences } from '../api/notifications-api';
import { isCategoryLive } from '../live-categories';
import styles from './NotificationPreferencesPage.module.css';

/**
 * NOTIF-02 — what reaches you, and how.
 *
 * **Per category, not per notification.** §L22 has seventy-two rows; a screen with
 * seventy-two pairs of switches is a screen nobody configures, and an unconfigured
 * screen means everybody keeps the defaults anyway. Nine categories is a set of
 * decisions somebody will actually make.
 *
 * **Escalations are listed and locked.** §L22's closing rule says they cannot be
 * disabled, and hiding them would leave somebody wondering why a message still
 * arrives after they switched the category off. Showing them with a padlock and a
 * sentence of explanation answers that before it is asked. The rule itself is
 * enforced on the server — the padlock is the explanation, not the enforcement.
 *
 * **One channel at a time** (Redesign Group 9): an "In app" tab and an "Email" tab, one switch per
 * category on each. The daily digest is an email setting, so it lives on the Email tab only, and an
 * escalation's padlock line shows only on the channel that escalation is sent on.
 *
 * **There is no Save button.** Every switch writes on the spot and the screen keeps
 * the new state (CLAUDE.md §5a rule 1); a failure rolls the switch back. A settings
 * screen that can be left half-saved is a settings screen that silently does not do
 * what somebody thinks it does.
 *
 * **There is also no local draft state.** The mutation writes the new value into
 * the query cache before the request leaves, so the cache *is* the optimistic
 * state and the switches flip on the frame they are clicked. Mirroring the query
 * into `useState` would mean two sources of truth for the same nine rows, plus an
 * effect to keep them in step — and an effect that calls `setState` is a cascading
 * render the linter is right to refuse.
 */
export function NotificationPreferencesPage(): ReactNode {
  const preferences = usePreferences();
  const update = useUpdatePreferences();
  const modules = useModules();
  const [channel, setChannel] = useState<Channel>('in_app');
  const current = preferences.data ?? null;

  if (preferences.isError) {
    return (
      <div className={styles.page}>
        <ServerErrorState
          {...(preferences.error.requestId ? { requestId: preferences.error.requestId } : {})}
          onRetry={() => void preferences.refetch()}
        />
      </div>
    );
  }

  const save = (next: NotificationPreferences): void => {
    update.mutate({ categories: [...next.categories], isDigestEnabled: next.isDigestEnabled });
  };

  const setCategory = (category: NotificationCategory, patch: Partial<CategoryPreference>): void => {
    if (!current) return;
    save({
      ...current,
      categories: current.categories.map((row) =>
        row.category === category ? { ...row, ...patch } : row,
      ),
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Notification settings</h1>
          <p className={styles.meta}>What reaches you, and how</p>
        </div>
        <Link to="/notifications" className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          Back to notifications
        </Link>
      </header>

      <Card isFlush>
        {!current ? (
          <div className={styles.loading}>
            <SkeletonLines count={8} />
          </div>
        ) : (
          <Tabs label="How notifications reach you" tabs={CHANNEL_TABS} value={channel} onChange={setChannel}>
            {channel === 'email' && (
              <SettingSwitch
                isDigest
                label="Send me one daily email instead of one per notification"
                description="Everything that is not urgent is collected and emailed at 8am. Urgent messages and escalations still reach you the moment they happen — a digest that could hold one of those back would be a digest that hides it. You keep seeing everything here, immediately, either way."
                checked={current.isDigestEnabled}
                onChange={(checked) => save({ ...current, isDigestEnabled: checked })}
              />
            )}
            {current.categories
              .filter((row) => isCategoryLive(row.category, modules))
              .map((row) => (
                <SettingSwitch
                  key={row.category}
                  label={NOTIFICATION_CATEGORY_LABELS[row.category]}
                  description={NOTIFICATION_CATEGORY_DESCRIPTIONS[row.category]}
                  lock={lockLine(row.category, channel, modules)}
                  checked={channel === 'email' ? row.isEmailEnabled : row.isInAppEnabled}
                  onChange={(checked) =>
                    setCategory(row.category, channel === 'email' ? { isEmailEnabled: checked } : { isInAppEnabled: checked })
                  }
                />
              ))}
          </Tabs>
        )}
      </Card>
    </div>
  );
}

type Channel = 'in_app' | 'email';

const CHANNEL_TABS: readonly TabItem<Channel>[] = [
  { value: 'in_app', label: 'In app' },
  { value: 'email', label: 'Email' },
];

/**
 * The padlock line, only where it is true: N-39, N-40 and N-43 go by email alone (§L22), so the
 * In app tab must not claim they always send there. Labels keep their capitals ("HR Lead").
 */
function lockLine(category: NotificationCategory, channel: Channel, modules: ModuleState): string | null {
  const labels = escalationsIn(category)
    .filter((definition) => definition.channels.includes(channel) && isNotificationOn(modules, definition.key))
    .map((definition) => definition.label);
  if (labels.length === 0) return null;
  return `${labels.length === 1 ? 'This always sends' : 'These always send'}: ${labels.join('; ')}.`;
}

function SettingSwitch({
  label,
  description,
  lock = null,
  isDigest = false,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly description: string;
  readonly lock?: string | null;
  /** The digest is a decision about the whole channel, so it sits first on a tinted row. */
  readonly isDigest?: boolean;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}): ReactNode {
  return (
    <div className={styles.row} data-digest={isDigest || undefined}>
      <div className={styles.rowText}>
        <span className={styles.rowName}>{label}</span>
        <span className={styles.rowDescription}>{description}</span>
        {lock !== null && (
          <p className={styles.locked}>
            <Lock size={13} aria-hidden />
            <span>{lock}</span>
          </p>
        )}
      </div>
      <div className={styles.control}>
        <span className={styles.state} aria-hidden>
          {checked ? 'On' : 'Off'}
        </span>
        <Switch checked={checked} onChange={onChange} ariaLabel={label} />
      </div>
    </div>
  );
}
